// Server Browser — tabbed server list with favorites, recents, and sorting

import type { ServerInfo } from "../../net/server.js";
import { listServers } from "../../engine/launcher.js";
import { FONT, PANEL_BG, BORDER, TEXT, TEXT_DIM, ACCENT, ROW_HOVER } from "./theme.js";
import { showPasswordPrompt } from "./password.js";
import { getFavorites, getRecents, toggleFavorite } from "./history.js";
import { MapSection } from "./mapsection.js";

export type BrowserCallbacks = {
  onJoin: (server: ServerInfo, password?: string) => void;
  onHost: () => void;
  onHostMap: (code: string) => void;
  onQuickPlay: (servers: ServerInfo[]) => void;
};

type Tab = "browse" | "favorites" | "recent";
type SortKey = "name" | "map" | "players" | "settings" | "mode" | "ping";

const GRID = "24px 2fr 1fr 80px 80px 1fr 50px";

export class ServerBrowser {
  private el: HTMLDivElement;
  private listEl: HTMLDivElement;
  private relayHttp: string;
  private refreshTimer = 0;
  private callbacks: BrowserCallbacks;
  private ping = -1;
  private latestServers: ServerInfo[] = [];
  private statusEl!: HTMLDivElement;
  private favorites: Set<string> = new Set();
  private recents: string[] = [];
  private activeTab: Tab = "browse";
  private tabButtons = new Map<Tab, HTMLButtonElement>();
  private sortKey: SortKey = "players";
  private sortAsc = false;
  private headerCells = new Map<SortKey, HTMLSpanElement>();
  private refreshBtn!: HTMLButtonElement;
  private mapSection: MapSection;

  constructor(relayHttp: string, callbacks: BrowserCallbacks) {
    this.relayHttp = relayHttp;
    this.callbacks = callbacks;

    this.el = document.createElement("div");
    this.el.style.cssText =
      `position:fixed;inset:0;display:flex;flex-direction:column;align-items:center;` +
      `z-index:1000;background:#0a0b0d;padding-top:6vh;overflow-y:auto;`;

    // Game title
    const gameTitle = document.createElement("div");
    gameTitle.textContent = "STRAFEPARK.GG";
    gameTitle.style.cssText = `font:bold 20px ${FONT};color:${TEXT};letter-spacing:2px;margin-bottom:32px;text-align:center;`;
    this.el.appendChild(gameTitle);

    // Header
    const header = document.createElement("div");
    header.style.cssText = `display:flex;align-items:center;gap:16px;margin-bottom:16px;`;

    const title = document.createElement("div");
    title.textContent = "SERVERS";
    title.style.cssText = `font:bold 18px ${FONT};color:${TEXT};letter-spacing:2px;`;
    header.appendChild(title);

    const spacer = document.createElement("div");
    spacer.style.cssText = "flex:1;";
    header.appendChild(spacer);

    const playBtn = document.createElement("button");
    playBtn.textContent = "PLAY";
    playBtn.style.cssText =
      `padding:6px 16px;background:${ACCENT};color:#fff;border:none;cursor:pointer;` +
      `font:bold 11px ${FONT};letter-spacing:1px;`;
    playBtn.addEventListener("click", () => callbacks.onQuickPlay(this.latestServers));
    header.appendChild(playBtn);

    const hostBtn = document.createElement("button");
    hostBtn.textContent = "HOST SERVER";
    hostBtn.style.cssText =
      `padding:6px 16px;background:transparent;color:${TEXT_DIM};border:1px solid ${BORDER};` +
      `cursor:pointer;font:bold 11px ${FONT};letter-spacing:1px;`;
    hostBtn.addEventListener("click", () => callbacks.onHost());
    header.appendChild(hostBtn);

    this.refreshBtn = document.createElement("button");
    this.refreshBtn.textContent = "REFRESH";
    this.refreshBtn.style.cssText =
      `padding:6px 12px;background:transparent;color:${TEXT_DIM};border:1px solid ${BORDER};` +
      `cursor:pointer;font:11px ${FONT};width:76px;height:28px;text-align:center;line-height:1;`;
    this.refreshBtn.addEventListener("click", () => this.manualRefresh());
    header.appendChild(this.refreshBtn);

    // Inject spinner keyframes once
    if (!document.getElementById("ironbolt-spinner-style")) {
      const style = document.createElement("style");
      style.id = "ironbolt-spinner-style";
      style.textContent = `@keyframes ib-spin { to { transform: rotate(360deg); } }`;
      document.head.appendChild(style);
    }

    this.el.appendChild(header);

    // Tabs
    const tabBar = document.createElement("div");
    tabBar.style.cssText = `display:flex;gap:0;margin-bottom:0;width:780px;`;

    const tabs: { key: Tab; label: string }[] = [
      { key: "browse", label: "BROWSE" },
      { key: "favorites", label: "FAVORITES" },
      { key: "recent", label: "RECENT" },
    ];

    for (const tab of tabs) {
      const btn = document.createElement("button");
      btn.textContent = tab.label;
      btn.style.cssText =
        `padding:7px 20px;border:1px solid ${BORDER};border-bottom:none;cursor:pointer;` +
        `font:bold 10px ${FONT};letter-spacing:1px;background:transparent;color:${TEXT_DIM};`;
      btn.addEventListener("click", () => this.setTab(tab.key));
      tabBar.appendChild(btn);
      this.tabButtons.set(tab.key, btn);
    }

    this.el.appendChild(tabBar);

    // Table container
    const container = document.createElement("div");
    container.style.cssText =
      `width:780px;max-height:50vh;overflow-y:auto;background:${PANEL_BG};border:1px solid ${BORDER};`;

    // Table header (sortable columns)
    const tableHeader = document.createElement("div");
    tableHeader.style.cssText =
      `display:grid;grid-template-columns:${GRID};padding:8px 12px;` +
      `border-bottom:1px solid ${BORDER};font:bold 10px ${FONT};color:${TEXT_DIM};` +
      `letter-spacing:1px;text-transform:uppercase;`;

    tableHeader.appendChild(document.createElement("span"));

    const columns: { key: SortKey; label: string }[] = [
      { key: "name", label: "Name" },
      { key: "map", label: "Map" },
      { key: "players", label: "Players" },
      { key: "settings", label: "Settings" },
      { key: "mode", label: "Mode" },
      { key: "ping", label: "Ping" },
    ];

    for (const col of columns) {
      const cell = document.createElement("span");
      cell.style.cssText = "cursor:pointer;user-select:none;";
      cell.textContent = col.label;
      cell.addEventListener("click", () => this.setSort(col.key));
      tableHeader.appendChild(cell);
      this.headerCells.set(col.key, cell);
    }
    this.updateHeaderLabels();

    container.appendChild(tableHeader);

    this.listEl = document.createElement("div");
    container.appendChild(this.listEl);

    this.el.appendChild(container);

    // Status line
    this.statusEl = document.createElement("div");
    this.statusEl.style.cssText = `margin-top:12px;font:11px ${FONT};color:${TEXT_DIM};min-height:16px;`;
    this.el.appendChild(this.statusEl);

    // Community maps section
    this.mapSection = new MapSection(relayHttp, {
      onHostMap: (code) => callbacks.onHostMap(code),
      onCreateMap: () => window.open("editor.html", "_blank"),
    });
  }

  mount(error?: string, mapCode?: string): void {
    document.body.appendChild(this.el);
    this.favorites = getFavorites();
    this.recents = getRecents();
    this.updateTabStyles();
    if (error) {
      this.statusEl.textContent = error;
      this.statusEl.style.color = "#e55";
    } else {
      this.statusEl.textContent = "";
    }
    this.refresh();
    this.measurePing();
    this.mapSection.mount(this.el);
    if (mapCode) this.mapSection.showMapByCode(mapCode);
    this.refreshTimer = window.setInterval(() => this.refresh(), 5000);
  }

  unmount(): void {
    this.mapSection.unmount();
    this.el.remove();
    clearInterval(this.refreshTimer);
  }

  private setTab(tab: Tab): void {
    this.activeTab = tab;
    this.favorites = getFavorites();
    this.recents = getRecents();
    this.updateTabStyles();
    this.renderList(this.latestServers);
  }

  private updateTabStyles(): void {
    for (const [key, btn] of this.tabButtons) {
      const active = key === this.activeTab;
      btn.style.background = active ? PANEL_BG : "transparent";
      btn.style.color = active ? TEXT : TEXT_DIM;
    }
  }

  private setSort(key: SortKey): void {
    if (this.sortKey === key) {
      this.sortAsc = !this.sortAsc;
    } else {
      this.sortKey = key;
      this.sortAsc = key !== "players" && key !== "ping";
    }
    this.updateHeaderLabels();
    this.renderList(this.latestServers);
  }

  private updateHeaderLabels(): void {
    const labels: Record<SortKey, string> = {
      name: "Name", map: "Map", players: "Players",
      settings: "Settings", mode: "Mode", ping: "Ping",
    };
    for (const [key, cell] of this.headerCells) {
      const arrow = this.sortKey === key ? (this.sortAsc ? " ^" : " v") : "";
      cell.textContent = labels[key] + arrow;
      cell.style.color = this.sortKey === key ? TEXT : TEXT_DIM;
    }
  }

  private async measurePing(): Promise<void> {
    try {
      const t0 = performance.now();
      await fetch(`${this.relayHttp}/ping`);
      this.ping = Math.round(performance.now() - t0);
    } catch {
      this.ping = -1;
    }
  }

  private async refresh(): Promise<void> {
    try {
      const servers = await listServers(this.relayHttp);
      this.latestServers = servers;
      this.renderList(servers);
      this.measurePing();
    } catch {
      this.latestServers = [];
      this.listEl.innerHTML = `<div style="padding:20px;color:${TEXT_DIM};font:11px ${FONT};text-align:center;">
        Could not reach relay server</div>`;
    }
  }

  private async manualRefresh(): Promise<void> {
    this.refreshBtn.textContent = "";
    const spinner = document.createElement("span");
    spinner.style.cssText =
      `display:inline-block;width:10px;height:10px;border:2px solid ${BORDER};` +
      `border-top-color:${TEXT_DIM};border-radius:50%;animation:ib-spin 0.6s linear infinite;`;
    this.refreshBtn.appendChild(spinner);
    this.refreshBtn.style.pointerEvents = "none";
    await Promise.all([this.refresh(), new Promise(r => setTimeout(r, 800))]);
    this.refreshBtn.textContent = "REFRESH";
    this.refreshBtn.style.pointerEvents = "";
  }

  private filterByTab(servers: ServerInfo[]): ServerInfo[] {
    switch (this.activeTab) {
      case "browse":
        return servers;
      case "favorites":
        return servers.filter(s => this.favorites.has(s.name));
      case "recent": {
        const recentSet = new Set(this.recents);
        return servers.filter(s => recentSet.has(s.name));
      }
    }
  }

  private sortValue(server: ServerInfo, key: SortKey): string | number {
    const gc = server.gameConfig as Record<string, unknown> | undefined;
    switch (key) {
      case "name": return server.name.toLowerCase();
      case "map": return server.mapName.toLowerCase();
      case "players": return server.playerCount;
      case "settings": return ((gc?.preset as string) ?? "classic").toLowerCase();
      case "mode": return ((gc?.mode as string) ?? "freeplay").toLowerCase();
      case "ping": return this.ping;
    }
  }

  private emptyMessage(): string {
    switch (this.activeTab) {
      case "browse": return "No servers running";
      case "favorites": return "No favorited servers online";
      case "recent": return "No recently joined servers online";
    }
  }

  private renderList(servers: ServerInfo[]): void {
    this.listEl.innerHTML = "";

    const filtered = this.filterByTab(servers);

    if (filtered.length === 0) {
      this.listEl.innerHTML = `<div style="padding:20px;color:${TEXT_DIM};font:11px ${FONT};text-align:center;">
        ${this.emptyMessage()}</div>`;
      return;
    }

    const dir = this.sortAsc ? 1 : -1;
    const sorted = [...filtered].sort((a, b) => {
      const va = this.sortValue(a, this.sortKey);
      const vb = this.sortValue(b, this.sortKey);
      if (va < vb) return -dir;
      if (va > vb) return dir;
      return 0;
    });

    for (const server of sorted) {
      const isFav = this.favorites.has(server.name);

      const row = document.createElement("div");
      row.style.cssText =
        `display:grid;grid-template-columns:${GRID};padding:8px 12px;` +
        `cursor:pointer;font:12px ${FONT};color:${TEXT};border-bottom:1px solid ${BORDER};` +
        `transition:background 0.1s;`;
      row.addEventListener("mouseenter", () => { row.style.background = ROW_HOVER; });
      row.addEventListener("mouseleave", () => { row.style.background = "transparent"; });
      row.addEventListener("click", async () => {
        if (server.hasPassword) {
          const pw = await showPasswordPrompt(server.name);
          if (pw === null) return;
          this.callbacks.onJoin(server, pw);
        } else {
          this.callbacks.onJoin(server);
        }
      });

      // Star toggle
      const star = document.createElement("span");
      star.textContent = isFav ? "\u2605" : "\u2606";
      star.style.cssText = `color:${isFav ? ACCENT : TEXT_DIM};cursor:pointer;font-size:13px;line-height:1;display:flex;align-items:center;justify-content:center;`;
      star.addEventListener("click", (e) => {
        e.stopPropagation();
        const nowFav = toggleFavorite(server.name, server.mapName);
        if (nowFav) this.favorites.add(server.name);
        else this.favorites.delete(server.name);
        this.renderList(this.latestServers);
      });

      const name = document.createElement("span");
      name.textContent = server.name;
      if (server.hasPassword) {
        const lock = document.createElement("span");
        lock.textContent = " [pw]";
        lock.style.color = TEXT_DIM;
        name.appendChild(lock);
      }
      name.style.cssText = "overflow:hidden;text-overflow:ellipsis;white-space:nowrap;";

      const map = document.createElement("span");
      map.textContent = server.mapName;
      map.style.color = TEXT_DIM;

      const players = document.createElement("span");
      players.textContent = `${server.playerCount}/${server.maxPlayers}`;
      const full = server.playerCount >= server.maxPlayers;
      players.style.color = full ? "#e55" : ACCENT;

      const gc = server.gameConfig as Record<string, unknown> | undefined;

      const settings = document.createElement("span");
      const presetKey = (gc?.preset as string) ?? "classic";
      settings.textContent = presetKey === "custom" ? "Custom" : (presetKey.charAt(0).toUpperCase() + presetKey.slice(1));
      settings.style.color = presetKey === "custom" ? "#c49555" : TEXT_DIM;

      const mode = document.createElement("span");
      mode.textContent = (gc?.mode as string) ?? "freeplay";
      mode.style.color = TEXT_DIM;

      const pingCell = document.createElement("span");
      if (this.ping >= 0) {
        pingCell.textContent = `${this.ping}`;
        pingCell.style.color = this.ping < 50 ? "#5cb47a" : this.ping < 100 ? ACCENT : "#c49555";
      } else {
        pingCell.textContent = "-";
        pingCell.style.color = TEXT_DIM;
      }

      row.append(star, name, map, players, settings, mode, pingCell);
      this.listEl.appendChild(row);
    }
  }
}
